import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getDTRListClassRosterIdWise } from "../../../services/learnPointUserServices";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Grid,
  IconButton,
} from "@mui/material";

import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AddIcon from "@mui/icons-material/Add";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import { styled } from "@mui/material/styles";

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const DtrList = (props) => {
  const navigate = useNavigate();
  const { classRosterId } = props;
  const [DtrData, setDtrData] = useState([]);
  const [noRecords, setNoRecords] = useState(true);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadRosterDetails = async () => {
      if (classRosterId) {
        try {
          await rosterDetails();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to load roster details:", error);
          }
        }
      }
    };

    loadRosterDetails();

    return () => {
      cancelled = true;
    };
  }, [classRosterId]);

  const rosterDetails = async () => {
    try {
      setLoading(true);
      const DtrListDetails = await getDTRListClassRosterIdWise(classRosterId);
      if (DtrListDetails.status === true) {
        setDtrData(DtrListDetails.data);
      }
      if (DtrListDetails?.data?.length > 0) {
        setNoRecords(false);
      } else {
        setNoRecords(true);
        setDtrData([]);
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occurred.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const DtrRedirection = useCallback(
    (dtrDate, classRosterId, isEditable) => {
      navigate(`/learnpoint/infoSection/dtrDetails`, {
        state: {
          classRosterId,
          dtrDate,
          isEditable,
        },
      });
    },
    [navigate],
  );

  return (
    <>
      {loading && <BackdropComponent />}
      <Grid container>
        <Grid item xs={4}>
          <TableContainer component={Paper}>
            <Table id="dtrList" size="small">
              <TableHead>
                <TableRow>
                  {["", "DTR Date", "Status", "Last Modified By"].map(
                    (header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: "white",
                          fontWeight: "bold",
                        }}
                      >
                        {header}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {DtrData.map((row, i) => {
                  const rowKey = `${row["DTRDate"]}-${i}`;
                  return (
                    <TableRow
                      key={rowKey}
                      sx={{
                        backgroundColor: i % 2 === 0 ? "#f9f9f9" : "inherit",
                      }}
                    >
                      <TableCell sx={{ fontSize: "1rem", width: "5px" }}>
                        {/* ASTT-7319: isEditable changed to true, was
                            false -- per direct request, this redirect
                            should land on ClassDetails.js's editable
                            Class Roster Ids search box, pre-filled with
                            this row's classRosterId, instead of a
                            separate read-only "Roster ID" label. The
                            pre-fill itself is handled entirely in
                            ClassDetails.js (matches against
                            filterRosterData once it loads), nothing more
                            needed here beyond this flag. */}
                        <IconButton
                          onClick={() =>
                            DtrRedirection(row["DTRDate"], classRosterId, true)
                          }
                          sx={{
                            border: "1px solid grey", 
                            borderRadius: "4px", 
                            padding: "2px", 
                          }}
                        >
                          <ExitToAppIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", width: "20px" }}>
                        {new Date(row["DTRDate"]).toLocaleDateString("en-US")}
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", width: "20px" }}>
                        {row["DTRStatus"]}
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", width: "150px" }}>
                        {row["ModifiedBy"]}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {noRecords && (
                  <TableRow>
                    <TableCell colSpan={4}>No DTRs to display.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={2}>
          <CustomButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => DtrRedirection(null, classRosterId, false)}
          >
            Add
          </CustomButton>
        </Grid>
        <Grid item xs={3.5}></Grid>
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
};

export default DtrList;
